import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Badge, Select, Money, Button, Modal, Field, Input, Card, DateText } from '../components/ui';
import { Can } from '../components/Can';

type S = Record<string, any>;
const today = () => new Date().toISOString().slice(0, 10);
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Accounting policy for a company — the answers to "how do you treat…", as data.
 *
 * Also the home of the two things people expect to find in a finance system and
 * usually cannot: the year-end close, and the honest position on linking to an
 * external ledger.
 */
export default function FinanceSettingsPage() {
  const qc = useQueryClient();
  const [entityId, setEntityId] = useState('');
  const [form, setForm] = useState<S | null>(null);
  const [closing, setClosing] = useState(false);
  const [closeForm, setCloseForm] = useState({ yearEnd: '', setLockDate: true });
  const [closeResult, setCloseResult] = useState<S | null>(null);
  const [exportForm, setExportForm] = useState({ from: `${new Date().getUTCFullYear()}-01-01`, to: today(), shape: 'JOURNALS' });

  const entities = useQuery({ queryKey: ['entities'], queryFn: async () => (await api.get<S[]>('/entities')).data });
  const target = entityId || entities.data?.[0]?.id || '';
  const settings = useQuery({ queryKey: ['finance-settings', target], queryFn: async () => (await api.get<S>('/finance-settings', { entityId: target })).data, enabled: !!target });
  const integrations = useQuery({ queryKey: ['accounting-integrations', target], queryFn: async () => (await api.get<S>('/integrations/accounting', { entityId: target })).data, enabled: !!target });
  const rates = useQuery({ queryKey: ['fx-rates'], queryFn: async () => (await api.get<S[]>('/fx-rates')).data });

  useEffect(() => { if (settings.data) setForm(settings.data); }, [settings.data]);

  const save = useMutation({
    mutationFn: async () => api.patch('/finance-settings', {
      entityId: target,
      fiscalYearStartMonth: Number(form!.fiscalYearStartMonth), fiscalYearStartDay: Number(form!.fiscalYearStartDay),
      vatRegistered: form!.vatRegistered, vatRegistrationNumber: form!.vatRegistrationNumber || null,
      vatScheme: form!.vatScheme, vatReturnFrequency: form!.vatReturnFrequency,
      partialExemptionMethod: form!.partialExemptionMethod,
      residualRecoveryPercent: form!.residualRecoveryPercent === null || form!.residualRecoveryPercent === '' ? null : Number(form!.residualRecoveryPercent),
      cisContractor: form!.cisContractor, cisUtr: form!.cisUtr || null, cisAoReference: form!.cisAoReference || null,
      defaultPaymentTermsDays: Number(form!.defaultPaymentTermsDays),
      dunningEnabled: form!.dunningEnabled,
      lockDate: form!.lockDate || null,
    }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance-settings'] }),
  });

  const closeYear = useMutation({
    mutationFn: async (dryRun: boolean) => (await api.post<S>('/year-end/close', { entityId: target, yearEnd: closeForm.yearEnd, setLockDate: closeForm.setLockDate, dryRun })).data,
    onSuccess: (r) => { setCloseResult(r); if (!r.dryRun) { setClosing(false); void qc.invalidateQueries({ queryKey: ['finance-settings'] }); } },
  });

  const connect = useMutation({
    mutationFn: async (provider: string) => api.put('/integrations/accounting', { entityId: target, provider, direction: 'EXPORT', scopes: ['JOURNAL_SUMMARY', 'TRIAL_BALANCE'] }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['accounting-integrations'] }),
  });

  const [exporting, setExporting] = useState(false);
  /**
   * The export endpoint is authenticated, so it cannot simply be opened in a tab —
   * the browser would send no bearer token and get a 401. Fetch it with the token
   * and hand the browser a blob instead.
   */
  const download = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ entityId: target, from: exportForm.from, to: exportForm.to, shape: exportForm.shape, format: 'CSV' });
      const base = import.meta.env.VITE_API_URL ?? '/api/v1';
      const stored = localStorage.getItem('lotus.auth');
      const token = stored ? (JSON.parse(stored) as { accessToken?: string }).accessToken : undefined;
      const res = await fetch(`${base}/integrations/accounting/export?${params.toString()}`, { headers: token ? { authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] ?? `ledger-${exportForm.shape.toLowerCase()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  if (!form) return <div><PageHeader title="Finance settings" subtitle="Loading…" /></div>;

  return (
    <div>
      <PageHeader
        title="Finance settings"
        subtitle="How this company keeps its books: the fiscal year, the VAT scheme, whether it is a CIS contractor, and where the line is drawn under a signed-off year."
        actions={<div className="flex items-center gap-2">
          <Select value={entityId} onChange={(e) => setEntityId(e.target.value)}>{entities.data?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select>
          <Can do="financeSettings.update"><Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button></Can>
        </div>}
      />

      {save.isError && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{(save.error as Error).message}</p>}
      {save.isSuccess && <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">Saved.</p>}

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="Financial year">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Year starts in"><Select value={form.fiscalYearStartMonth} onChange={(e) => setForm({ ...form, fiscalYearStartMonth: e.target.value })} className="w-full">{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</Select></Field>
            <Field label="On day"><Input type="number" min="1" max="31" value={form.fiscalYearStartDay} onChange={(e) => setForm({ ...form, fiscalYearStartDay: e.target.value })} className="w-full" /></Field>
          </div>
          <div className="mt-3">
            <Field label="Hard lock date" hint="Nothing may post on or before this date, whatever the period status says. Set by the year-end close.">
              <Input type="date" value={form.lockDate ?? ''} onChange={(e) => setForm({ ...form, lockDate: e.target.value })} className="w-full" />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Default payment terms (days)"><Input type="number" value={form.defaultPaymentTermsDays} onChange={(e) => setForm({ ...form, defaultPaymentTermsDays: e.target.value })} className="w-full" /></Field>
          </div>
        </Card>

        <Card title="VAT">
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-[13px] text-ink-700"><input type="checkbox" checked={form.vatRegistered} onChange={(e) => setForm({ ...form, vatRegistered: e.target.checked })} /> Registered for VAT</label>
            <Field label="Registration number"><Input value={form.vatRegistrationNumber ?? ''} onChange={(e) => setForm({ ...form, vatRegistrationNumber: e.target.value })} className="w-full" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Scheme"><Select value={form.vatScheme} onChange={(e) => setForm({ ...form, vatScheme: e.target.value })} className="w-full"><option value="STANDARD">Standard (accrual)</option><option value="CASH">Cash accounting</option><option value="FLAT_RATE">Flat rate</option><option value="NONE">Not registered</option></Select></Field>
              <Field label="Return frequency"><Select value={form.vatReturnFrequency} onChange={(e) => setForm({ ...form, vatReturnFrequency: e.target.value })} className="w-full"><option value="MONTHLY">Monthly</option><option value="QUARTERLY">Quarterly</option><option value="ANNUAL">Annual</option></Select></Field>
            </div>
            <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-3.5">
              <p className="mb-2 text-[12.5px] leading-relaxed text-ink-600">
                <strong className="font-medium text-ink-900">Partial exemption.</strong> Residential letting is exempt and new-build first grants are zero-rated, so a build-to-rent group is partially exempt: residual input tax must be apportioned under a method <em>agreed with the tax authority</em>. The platform applies the method you agreed; it never invents one.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Agreed method"><Select value={form.partialExemptionMethod} onChange={(e) => setForm({ ...form, partialExemptionMethod: e.target.value })} className="w-full"><option value="NONE">None agreed — recover nothing residual</option><option value="STANDARD">Standard method</option><option value="SPECIAL">Special method</option></Select></Field>
                <Field label="Residual recovery %"><Input type="number" step="0.1" min="0" max="100" value={form.residualRecoveryPercent ?? ''} onChange={(e) => setForm({ ...form, residualRecoveryPercent: e.target.value })} disabled={form.partialExemptionMethod === 'NONE'} className="w-full" /></Field>
              </div>
            </div>
          </div>
        </Card>

        <Card title="Construction Industry Scheme">
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-[13px] text-ink-700"><input type="checkbox" checked={form.cisContractor} onChange={(e) => setForm({ ...form, cisContractor: e.target.checked })} /> This company is a CIS contractor</label>
            <p className="text-[12.5px] leading-relaxed text-ink-500">With this on, an approved bill carrying labour from a subcontractor in the scheme withholds 20% or 30% of the labour element and books it as a liability to HMRC.</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contractor UTR"><Input value={form.cisUtr ?? ''} onChange={(e) => setForm({ ...form, cisUtr: e.target.value })} className="w-full" /></Field>
              <Field label="Accounts office reference"><Input value={form.cisAoReference ?? ''} onChange={(e) => setForm({ ...form, cisAoReference: e.target.value })} className="w-full" /></Field>
            </div>
          </div>
        </Card>

        <Card title="Chasing arrears">
          <label className="flex items-center gap-2 text-[13px] text-ink-700"><input type="checkbox" checked={form.dunningEnabled} onChange={(e) => setForm({ ...form, dunningEnabled: e.target.checked })} /> Send automatic payment reminders</label>
          <p className="mt-2 text-[12.5px] leading-relaxed text-ink-500">The ladder below is data — change the days and nobody is chased on the old schedule. Every send is deduped on invoice and stage, so a retried job cannot chase the same debt twice.</p>
          <ul className="mt-3 space-y-1.5 text-[13px]">
            {(form.dunningSchedule ?? []).map((s: S) => (
              <li key={s.stage} className="flex items-center justify-between rounded-lg bg-ink-50/70 px-3 py-2"><span className="text-ink-700">Stage {s.stage} · {s.daysAfterDue} days after due</span><span className="text-[11.5px] text-ink-400">{s.channel}</span></li>
            ))}
          </ul>
        </Card>

        <Card title="Year-end close" subtitle="Zeroes the P&L and carries the result to retained earnings">
          <p className="mb-3 text-[12.5px] leading-relaxed text-ink-600">
            Income and expense accounts measure a period, not a position — on day one of the new year they have to start at nil. Without the closing journal, year two shows year one's income and the balance sheet double-counts the profit.
          </p>
          <Can do="periods.closeYear">
            <Button variant="secondary" onClick={() => { setCloseForm({ yearEnd: '', setLockDate: true }); setCloseResult(null); setClosing(true); }}>Run the year end</Button>
          </Can>
        </Card>

        <Card title="Exchange rates" subtitle="A document is translated at the rate on its own tax point">
          {!rates.data?.length ? <p className="text-[13px] text-ink-500">No rates entered yet.</p> : (
            <table className="w-full text-[12.5px]">
              <tbody>{rates.data.slice(0, 8).map((r) => (
                <tr key={r.id} className="border-b border-ink-50 last:border-0"><td className="py-1.5 text-ink-600">{r.rateDate}</td><td className="py-1.5 text-ink-800">{r.fromCurrency} → {r.toCurrency}</td><td className="py-1.5 text-right tabular-nums">{r.rate}</td><td className="py-1.5 pl-3 text-right text-[11px] text-ink-400">{r.source}</td></tr>
              ))}</tbody>
            </table>
          )}
        </Card>
      </div>

      {/* External accounting */}
      <Card className="mt-5" title="Linking to another accounting package" subtitle="The books live here — this is the export seam">
        <p className="mb-4 max-w-3xl text-[12.5px] leading-relaxed text-ink-600">
          Nothing in this platform depends on Xero, QuickBooks, Sage or Zoho being present: the general ledger, the VAT return and the CIS return are all computed from this database. Some companies already run something else and their accountant wants the numbers in it — so a period can be exported in the shape those packages import.
          <br /><br />
          <strong className="font-medium text-ink-900">Live two-way synchronisation is not implemented.</strong> Each provider needs a registered application, per-company OAuth consent and a credential store. A connection here records the intent and the mapping so adding an adapter later is a contained change; it does not pretend to be connected.
        </p>

        <div className="mb-5 flex flex-wrap items-end gap-2">
          <Field label="From"><Input type="date" value={exportForm.from} onChange={(e) => setExportForm({ ...exportForm, from: e.target.value })} /></Field>
          <Field label="To"><Input type="date" value={exportForm.to} onChange={(e) => setExportForm({ ...exportForm, to: e.target.value })} /></Field>
          <Field label="Shape"><Select value={exportForm.shape} onChange={(e) => setExportForm({ ...exportForm, shape: e.target.value })}>
            <option value="JOURNALS">Journal lines</option><option value="SUMMARY">Monthly account summary</option><option value="TRIAL_BALANCE">Trial balance</option><option value="INVOICES">Invoices</option><option value="BILLS">Bills</option>
          </Select></Field>
          <Can do="glReports.export"><Button icon="Download" onClick={() => void download()} disabled={exporting}>{exporting ? 'Exporting…' : 'Export CSV'}</Button></Can>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {integrations.data?.catalogue?.map((p: S) => {
            const conn = integrations.data.connections?.find((c: S) => c.provider === p.provider);
            return (
              <div key={p.provider} className="rounded-xl border border-ink-200 bg-white p-3.5">
                <div className="flex items-center justify-between"><span className="font-medium text-ink-900">{p.label}</span>{conn ? <Badge value={conn.status} tone="neutral" /> : null}</div>
                <p className="mt-1 text-[11.5px] leading-relaxed text-ink-500">{p.notes}</p>
                {p.provider !== 'CSV' && (
                  <Can do="integrations.update">
                    <Button size="sm" variant="ghost" className="mt-2 px-0" onClick={() => connect.mutate(p.provider)} disabled={connect.isPending}>{conn ? 'Update mapping' : 'Record this connection'}</Button>
                  </Can>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Year end */}
      <Modal open={closing} onClose={() => setClosing(false)} size="lg" title="Year-end close" footer={
        closeResult?.ready && closeResult.dryRun
          ? <><Button variant="secondary" onClick={() => setClosing(false)}>Cancel</Button><Button onClick={() => closeYear.mutate(false)} disabled={closeYear.isPending}>{closeYear.isPending ? 'Closing…' : 'Post the closing journal'}</Button></>
          : <><Button variant="secondary" onClick={() => setClosing(false)}>Close</Button><Button onClick={() => closeYear.mutate(true)} disabled={!closeForm.yearEnd || closeYear.isPending}>Check the year</Button></>
      }>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Last day of the year"><Input type="date" value={closeForm.yearEnd} onChange={(e) => { setCloseForm({ ...closeForm, yearEnd: e.target.value }); setCloseResult(null); }} className="w-full" /></Field>
            <Field label="Lock the books after"><label className="flex h-[42px] items-center gap-2 text-[13px] text-ink-700"><input type="checkbox" checked={closeForm.setLockDate} onChange={(e) => setCloseForm({ ...closeForm, setLockDate: e.target.checked })} /> Set the hard lock date</label></Field>
          </div>

          {closeYear.isError && <p className="rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{(closeYear.error as Error).message}</p>}

          {closeResult && (
            <>
              <ul className="space-y-1.5">
                {closeResult.checks.map((c: S) => (
                  <li key={c.key} className="flex items-start gap-2 text-[12.5px]">
                    <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${c.passed ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    <span><span className="text-ink-800">{c.label}</span> — <span className="text-ink-500">{c.detail}</span></span>
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-3 gap-3">
                {[['Income', closeResult.incomeMinor], ['Expenses', closeResult.expenseMinor], [closeResult.resultMinor >= 0 ? 'Profit' : 'Loss', closeResult.resultMinor]].map(([l, val]) => (
                  <div key={String(l)} className="rounded-xl bg-ink-50/70 p-3"><div className="text-[11px] text-ink-500">{l}</div><div className="mt-0.5 font-semibold tabular-nums"><Money minor={val as number} /></div></div>
                ))}
              </div>
              <p className="text-[12.5px] text-ink-600">
                {closeResult.lines.length} P&amp;L account{closeResult.lines.length === 1 ? '' : 's'} will be posted back to nil, with the result carried to{' '}
                <strong>{closeResult.retainedEarningsAccount ? `${closeResult.retainedEarningsAccount.code} ${closeResult.retainedEarningsAccount.name}` : 'retained earnings'}</strong>.
                {closeResult.journalReference && <> Posted as <strong>{closeResult.journalReference}</strong>; {closeResult.periodsClosed} period(s) closed{closeResult.lockDateSet ? `, books locked to ${closeResult.lockDateSet}` : ''}.</>}
              </p>
            </>
          )}
        </div>
      </Modal>

      {settings.data?.lockDate && (
        <p className="mt-5 text-[12px] text-ink-400">Books are locked to <DateText value={settings.data.lockDate} />. Nothing may post on or before that date.</p>
      )}
    </div>
  );
}
